/**
 * Search Input Component
 * Text input for filtering work queue by search query
 * Uses debouncing for smooth typing experience
 */

import { forwardRef, useState, useEffect } from 'react'
import { Input, makeStyles, tokens } from '@fluentui/react-components'
import { SearchRegular } from '@fluentui/react-icons'
import type { ChangeEvent } from 'react'
import { useWorkQueueFilter } from '../context'
import { useDebounce } from '../../../hooks'

const DEBOUNCE_DELAY = 300

const useStyles = makeStyles({
  root: {
    minWidth: '200px',
    maxWidth: '300px',
    backgroundColor: '#f0f4ff',
    borderRadius: tokens.borderRadiusMedium,
    // Remove default borders that may cause focus issues
    border: 'none',
    '&:focus-within': {
      border: 'none',
      outline: 'none',
    },
    '& input': {
      backgroundColor: 'transparent',
      color: '#333333',
      '::placeholder': {
        color: 'rgba(0, 0, 0, 0.5)',
      },
    },
    '& .fui-Input__contentBefore': {
      color: 'rgba(0, 0, 0, 0.6)',
    },
  },
})

export interface SearchInputProps {
  placeholder?: string
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  function SearchInput({ placeholder = 'Search issues...' }, ref) {
    const styles = useStyles()
    const { searchQuery, setSearchQuery } = useWorkQueueFilter()

    // Local state for immediate UI feedback
    const [inputValue, setInputValue] = useState(searchQuery)

    // Debounced value for context updates
    const debouncedValue = useDebounce(inputValue, DEBOUNCE_DELAY)

    // Sync debounced value to context
    useEffect(() => {
      setSearchQuery(debouncedValue)
    }, [debouncedValue, setSearchQuery])

    // Sync context to local state when it changes externally (e.g., clear filter)
    useEffect(() => {
      if (searchQuery !== inputValue && searchQuery !== debouncedValue) {
        setInputValue(searchQuery)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync on external searchQuery changes
    }, [searchQuery])

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value)
    }

    return (
      <Input
        ref={ref}
        className={styles.root}
        contentBefore={<SearchRegular />}
        placeholder={placeholder}
        value={inputValue}
        onChange={handleChange}
        appearance="filled-darker"
        aria-label="Search issues (Ctrl+K or /)"
      />
    )
  }
)
